<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from "vue";
import { useTranslation } from "i18next-vue";

import type { Headers, PaginationMeta } from "@/types/rest";
import type { Activity } from "@/types/activity";
import type { ChangeCallbackParams } from "@/types/config";
import type {
  IntegrationInstance,
  EntityFilterData,
  ConfiguredEntity
} from "@/types/integrationInstance";

import type { ActivityGroup } from "@/types/activityGroup";

import { activityGroupsStore } from "@/stores/activityGroups";
import { activitiesStore } from "@/stores/activities";
import { integrationsStore } from "@/stores/integrations";
import { addErrorBottom } from "@/stores/messages";

import {
  getCurrentLocale,
  getValueByLang
} from "@/mixins/translatedProperty";
import { useComponentErrorCapture } from "@/mixins/error";
import router from "@/mixins/router";
import { getActivityGroupOptions } from "@/mixins/activities";
import { useDataHelper } from "@/mixins/dataHelper";
import { getPaginationLimit, savePaginationLimit } from "@/mixins/listing";
import { isTouchEnabled } from "@/mixins/device";

import UCInput from "@/components/ui/UCInput.vue";
import UCSelect from "@/components/ui/UCSelect.vue";
import IconSelect from "@/components/elements/icon/IconSelect.vue";
import IncludedEntities from "@/components/elements/entity/IncludedEntities.vue";
import EntityListFiltered from "@/components/elements/entity/EntityListFiltered.vue";

const { t } = useTranslation();
useComponentErrorCapture();
const { updateObjectByKeys, standardizeLangTexts } = useDataHelper();

const storage = activityGroupsStore();
const activitiesStorage = activitiesStore();
const integrationsStorage = integrationsStore();

const props = defineProps({
  groupId: {
    type: String,
    required: true,
  },
});

const activityGroup = ref<ActivityGroup | null>(null);
const actiGroupValues = ref<Record<string, any>>({});

const showAvailableEntities = ref(false);

const filteredActivities = ref<ConfiguredEntity[]>([]);
const includedActivities = ref<Activity[]>([]);
const instances = ref<IntegrationInstance[]>([]);

const activityGroupIncludedList = ref();
const actiGroupAvailableEntities = ref();

const activityListFilter = ref(<EntityFilterData>{
  searchText: '',
});

const activityGroupOptions = ref(getActivityGroupOptions(t))

const loading = ref(false);

const pagination = ref<PaginationMeta>({
  limit: getPaginationLimit() ?? 20,
  page: 1,
});

storage.$onAction(({ name, args, after }) => {
  if (name !== "socketUpdate") {
    return;
  }
  after(async () => {
    const { group_id, event_type } = args[0];
    if (group_id !== props.groupId) {
      return;
    }
    if (event_type === "DELETE") {
      router.push({
        name: "activities-macros",
      });
    } else if (
      group_id === props.groupId &&
      event_type === "CHANGE" &&
      (args[0] && args[0].new_state) 
    ) {
      const updActivityGroup = updateObjectByKeys(JSON.parse(JSON.stringify(activityGroup.value)), args[0].new_state);
      setActivityGroup(updActivityGroup);
    }
  });
});

integrationsStorage.$subscribe((mutation, state) => {
  instances.value = state.instances;
});

watch(showAvailableEntities, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const container = actiGroupAvailableEntities.value as HTMLElement;
      if (!isTouchEnabled() && container) {
        const firstInput = container.querySelector('input') as HTMLElement;
        firstInput?.focus();
      }
    });
  }
});

function setActivityGroup(newValue: ActivityGroup, onInit: boolean = false) {
  const nameLang = actiGroupValues.value.name?.langCode ?? getCurrentLocale();
  const descLang = actiGroupValues.value.description?.langCode ?? getCurrentLocale();
  
  activityGroup.value = newValue;

  const actiGrName = getValueByLang(newValue.name, nameLang, !onInit);
  const actiGrDescr = getValueByLang(newValue.description, descLang, !onInit);

  actiGroupValues.value = {
    icon: newValue.icon,
    name: {
      value: actiGrName.value,
      langCode: actiGrName.lang
    },
    description: {
      value: actiGrDescr.value,
      langCode: actiGrDescr.lang
    },
    removeTurnOnDelays: newValue.options?.remove_turn_on_delays
      ? {
          value: newValue.options.remove_turn_on_delays,
          label: t(`activity_group.options.remove_turn_on_delays.options.${newValue.options.remove_turn_on_delays}`)
        }
      : { value: "", label: "" },
    turnOffUnusedEntities: newValue.options?.turn_off_unused_entities
      ? {
          value: newValue.options.turn_off_unused_entities,
          label: t(`activity_group.options.turn_off_unused_entities.options.${newValue.options.turn_off_unused_entities}`)
        }
      : { value: "", label: "" }
  };

  includedActivities.value = newValue.activities || [];

  activityGroupIncludedList.value?.updateSelected(includedActivities.value);
}

watch(
  () => pagination.value.limit,
  (val) => {
    savePaginationLimit(val);
  }
)

function changeItemIcon(change: ChangeCallbackParams) {
  actiGroupValues.value.icon = change.value as string;

  if (!activityGroup.value || activityGroup.value == null) {
    return;
  }

  const newValues = {
    ...activityGroup.value,
    icon: actiGroupValues.value.icon,
  } as ActivityGroup;

  submitChange(newValues);
}

function changeItemName(message: any) {
  if (!activityGroup.value || activityGroup.value == null) {
    return;
  }

  const name = standardizeLangTexts({
    ...(activityGroup.value.name || {}),
    [message.langCode]: message.value,
  }, message.langCode);

  const newValues = {
    ...activityGroup.value,
    name: name,
  } as ActivityGroup;

  submitChange(newValues);
}

function changeItemDescription(message: any) {
  if (!activityGroup.value || activityGroup.value == null) {
    return;
  }

  const description = standardizeLangTexts({
    ...(activityGroup.value.description || {}),
    [message.langCode]: message.value,
  }, message.langCode);

  const newValues = {
    ...activityGroup.value,
    description: description,
  } as ActivityGroup;

  submitChange(newValues);
}


// function changeActivityGroup(item: any) {
//   const selectedGroup = activityGroups.value.find(g => g.group_id == item.value);

//   if (!activityGroup.value || activityGroup.value == null || !activityGroup.value.options || !selectedGroup?.group_id) {
//     return;
//   }

//   let actiOptions = activityGroup.value.options;

//   actiOptions['activity_group'] = {
//     group_id: selectedGroup?.group_id,
//     name: selectedGroup?.name,
//   }

//   const newValues = {
//     ...activityGroup.value,
//     options: actiOptions,
//   } as Activity;

//   submitChange(newValues);
// }

async function submitChange(message: ActivityGroup) {
  if (!activityGroup.value) {
    return;
  }

  try {
    activityGroup.value = await storage.update(
      activityGroup.value.group_id,
      message
    ) as ActivityGroup;
  } catch (e: any) {    
    addErrorBottom(e, 'activity_group.update');
  }
}

function changeItemNameLang(lang: string) {
  actiGroupValues.value.name.langCode = lang;

  if (activityGroup.value) {
    actiGroupValues.value.name.value = getValueByLang(activityGroup.value.name, lang, true).value;
  }
}

function changeItemDescriptionLang(lang: string) {
  actiGroupValues.value.description.langCode = lang;
  
  if (activityGroup.value) {
    actiGroupValues.value.description.value = getValueByLang(activityGroup.value.description, lang, true).value;
  }
}

function changeRemoveTurnOnDelays(item: any) {
  actiGroupValues.value.removeTurnOnDelays = item;
  saveActivityGroupOptions();
}

function changeTurnOffUnusedEntities(item: any) {
  actiGroupValues.value.turnOffUnusedEntities = item;
  saveActivityGroupOptions();
}

async function saveActivityGroupOptions() {
  if (activityGroup.value == null) {
    return;
  }
  
  try {
    await storage.update(
      activityGroup.value.group_id,
      {
        options: {
          ...(actiGroupValues.value.removeTurnOnDelays?.value && {remove_turn_on_delays: actiGroupValues.value.removeTurnOnDelays?.value}),
          ...(actiGroupValues.value.turnOffUnusedEntities?.value && {turn_off_unused_entities: actiGroupValues.value.turnOffUnusedEntities?.value})
        }
      }
    );
  } catch (e: any) { 
    addErrorBottom(e, 'activity_group.update');
  }
}

async function setDefaults() {
  instances.value = await integrationsStorage.getInstances();
}

async function includedActivityListChanged(newList: Activity[]) {
  if (!activityGroup.value) {
    return;
  }

  includedActivities.value = newList;

  const activity_ids = includedActivities.value.map((activity: Activity) => {
    return activity.entity_id;
  });

  try {
    activityGroup.value = await storage.update(
      activityGroup.value.group_id,
      {
        activity_ids: activity_ids
      }
    ) as ActivityGroup;
    reloadIncludedActivities();
  } catch (e: any) {    
    addErrorBottom(e, 'activity_group.update');
  }
}

function addActivitiesToGroup(activities: Activity[]) {
  const newList = JSON.parse(JSON.stringify(includedActivities.value)).concat(activities);
  includedActivityListChanged(newList);
}

async function reloadIncludedActivities() {
  fetchFilteredActivites(true);
}

function changeFilter(data: EntityFilterData) {
  activityListFilter.value = data;
  fetchFilteredActivites(true);
}

async function fetchFilteredActivites(userFetchFirstPage: boolean = false) {
  if (userFetchFirstPage === true) {
    pagination.value.page = 1;
  }

  let searchText = activityListFilter.value.searchText;

  try {
    const entList = await activitiesStorage.getActivitiesByPageByLimit(pagination.value.page, pagination.value.limit, searchText, false);
    filteredActivities.value = entList.data.activities as ConfiguredEntity[];

    const listHeaders = entList.headers as Headers;
    if (listHeaders) {
      const headerLimit = Number(listHeaders['pagination-limit']);
      const newLimit = headerLimit <= Number(listHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
      pagination.value = {
        count: Number(listHeaders['pagination-count']) || 0,
        limit: newLimit || 0,
        page: Number(listHeaders['pagination-page']) || 0,
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchFilteredActivites();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchFilteredActivites();
}

onMounted(async () => {
  loading.value = true;
  try {
    const newValue = await storage.getActivityGroup(props.groupId);
    await setDefaults();
    setActivityGroup(newValue, true);
    fetchFilteredActivites(true);
  } catch (e: any) {
    addErrorBottom(e);
  }
  loading.value = false;
})

</script>
<template>
  <div class="ep-settings">
    <div class="ep-settings__form panel-col panel-col--40" v-overflow-indicator>
      <div class="ep-settings__form__wrapper">
        <div class="ep-settings__form__header">
          <IconSelect
            :key="actiGroupValues && actiGroupValues.icon ? actiGroupValues.icon : 'fa-light fa-layer-group'"
            :value="actiGroupValues && actiGroupValues.icon ? actiGroupValues.icon : ''"
            :fallback="'fa-light fa-layer-group'"
            :change-callback="changeItemIcon"
          />
        </div>
        <UCInput
          v-if="actiGroupValues.name"
          v-model="actiGroupValues.name"
          @submit="changeItemName"
          @changeLang="changeItemNameLang"
          :translations="activityGroup?.name"
          :has-lang="true"
          :full-w="true"
          :label="$t('form.name')"
        />
        <UCInput
          v-if="actiGroupValues.description"
          v-model="actiGroupValues.description"
          @submit="changeItemDescription"
          @changeLang="changeItemDescriptionLang"
          :translations="activityGroup?.description"
          :type="'textarea'"
          :has-lang="true"
          :full-w="true"
          :label="$t('form.description')"
        />
        <div class="form-item form-item--select">
          <span class="form-item--select__label">{{ $t('activity_group.options.remove_turn_on_delays.title') }}</span>
          <UCSelect
            v-if="actiGroupValues.removeTurnOnDelays"
            v-model="actiGroupValues.removeTurnOnDelays"
            :options="activityGroupOptions.removeTurnOnDelaysOptions"
            :light="true"
            :dynamic-width="true"
            :dynamic-position="true"
            @select="changeRemoveTurnOnDelays"
          />
        </div>
        <div class="form-item form-item--select  form-item--select--no-border">
          <span class="form-item--select__label">{{ $t('activity_group.options.turn_off_unused_entities.title') }}</span>
          <UCSelect
            v-if="actiGroupValues.removeTurnOnDelays"
            v-model="actiGroupValues.turnOffUnusedEntities"
            :options="activityGroupOptions.turnOffUnusedEntitiesOptions"
            :light="true"
            :dynamic-width="true"
            :dynamic-position="true"
            @select="changeTurnOffUnusedEntities"
          />
        </div>
      </div>
    </div>
    <div class="ep-settings__included-entities panel-col panel-col--60">
      <div class="ep-settings__included-entities__wrapper">
        <IncludedEntities
          ref="activityGroupIncludedList"
          @entity-list-changed="includedActivityListChanged"
          @reload-entities="reloadIncludedActivities"
          @click-add="showAvailableEntities = true"
          :entities="includedActivities"
          :instances="instances"
          :show-button-add="showAvailableEntities == false"
          :drag-group="'activity-group-entities'"
          :title="$t('activity_group.edit.included_activities')"
          :loading="loading"
          :text-add-first-title="$t('activity_group.edit.add_first.title')"
          :text-add-first-descr="$t('activity_group.edit.add_first.descr')"
        />
      </div>
    </div>
    <Transition :name="showAvailableEntities == true ? 'slide-tab-right' : 'slide-tab-left'">
      <div
        v-show="showAvailableEntities == true"
        ref="actiGroupAvailableEntities"
        class="ep-settings__available-entities panel-col panel-col--40"
      >
        <div class="ep-settings__available-entities__wrapper">
          <div class="ep-settings__available-entities__header">
            <button @click="showAvailableEntities = false" class="button button--blank button--icon">
              <i class="fa-light fa-arrow-left"></i>
            </button>
            <span>{{ $t("activity_group.edit.available_activities") }}</span>
          </div>
          <EntityListFiltered
            @add-entities="addActivitiesToGroup"
            @change-filter="changeFilter"
            @reload-entities="reloadIncludedActivities"
            @change-page="changePage"
            @change-per-page="changePerPage"
            :pagination="pagination"
            :all-entities="filteredActivities"
            :instances="instances"
            :drag-group="'activity-group-entities'"
            :has-dropdown-filter="false"
            :has-dropdown-menu="false"
            :has-quick-options="true"
            :parent="'edit-activity-group'"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>
